import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import {
  useLazySearchPsfInpatientHospitalsQuery,
  usePreviewCmsInpatientMutation,
} from "../../../store/queries/baseRateFiles";
import {
  ICmsDerivedFactors,
  ICmsPsfOverrides,
  ICmsToggleConfig,
  IPsfInpatientHospital,
  IPsfInpatientRow,
} from "../../../types/baseRateFile";
import { DEFAULT_CMS_TOGGLES } from "../utils/constants";

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--surface-border);
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
`;

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem 1rem;
`;

const ToggleGroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem 1.25rem;
  margin-top: 0.5rem;
`;

const SearchResultRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  cursor: pointer;
  background-color: var(--surface-card);

  &:hover {
    background-color: var(--surface-hover);
  }

  &.is-selected {
    border-color: var(--primary-color);
    background-color: var(--highlight-bg);
  }
`;

function ToggleCheckbox({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  /** Optional right-side detail shown next to the label (e.g. "× 4.405% factor", "$1,275.18 / claim"). */
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      className="flex align-items-center justify-content-between"
      style={{ gap: "0.75rem", padding: "0.25rem 0" }}
    >
      <div className="flex align-items-center" style={{ minWidth: 0 }}>
        <Checkbox
          inputId={id}
          checked={checked}
          onChange={(e) => onChange(e.checked ?? false)}
        />
        <label
          htmlFor={id}
          className="ml-2 text-sm"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </label>
      </div>
      {hint && (
        <small
          className="text-500"
          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {hint}
        </small>
      )}
    </div>
  );
}

const ScenarioCaret = styled.span`
  display: inline-block;
  transition: transform 150ms ease;
  margin-right: 0.5rem;
  &.is-open {
    transform: rotate(90deg);
  }
`;

const CollapsibleHeader = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--surface-border);
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
`;

function CollapsibleSection({
  title,
  defaultCollapsed = false,
  children,
}: {
  title: React.ReactNode;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  return (
    <>
      <CollapsibleHeader onClick={() => setOpen((o) => !o)}>
        <ScenarioCaret className={open ? "is-open" : ""}>▸</ScenarioCaret>
        {title}
      </CollapsibleHeader>
      {open && children}
    </>
  );
}

/** Friendly display labels for the std-amounts scenario keys. */
const SCENARIO_LABELS: Record<string, string> = {
  quality_ehr: "Quality Data + EHR User",
  quality_no_ehr: "Quality Data, no EHR",
  no_quality_ehr: "No Quality Data, EHR User",
  no_quality_no_ehr: "No Quality Data, no EHR",
};

function hspHint(factors: ICmsDerivedFactors | null): string | undefined {
  if (!factors) return undefined;
  return factors.appliesHsp ? "applies (SCH/MDH/EACH)" : "not applicable";
}

interface IProps {
  year: number;
  providerCcn: string;
  onProviderCcnChange: (ccn: string) => void;
  toggles: ICmsToggleConfig;
  onTogglesChange: (toggles: ICmsToggleConfig) => void;
  overrides: ICmsPsfOverrides;
  onOverridesChange: (overrides: ICmsPsfOverrides) => void;
  /** Reports the derived factors so the parent can include them in metadata if needed. */
  onPreviewLoaded?: (
    psf: IPsfInpatientRow | null,
    factors: ICmsDerivedFactors | null
  ) => void;
}

/**
 * Hospital Inpatient CMS — type-specific section of the Create Base Rate File modal.
 *
 * Flow:
 *   1. User searches hospital by name or CCN → calls /psf-inpatient/search.
 *   2. On select, we POST to /cms/preview to get the PSF row + derived factors.
 *   3. Editable inputs are bound to a Partial<IPsfInpatientRow> override blob,
 *      pre-filled from PSF. Any edit goes into `overrides` and is sent to the
 *      backend on Create.
 *   4. Toggle tree controls which line items get summed into each per-DRG rate.
 */
function HospitalInpatientCmsFields({
  year,
  providerCcn,
  onProviderCcnChange,
  toggles,
  onTogglesChange,
  overrides,
  onOverridesChange,
  onPreviewLoaded,
}: IProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<IPsfInpatientHospital[]>(
    []
  );
  const [selectedHospital, setSelectedHospital] =
    useState<IPsfInpatientHospital | null>(null);
  const [psf, setPsf] = useState<IPsfInpatientRow | null>(null);
  const [factors, setFactors] = useState<ICmsDerivedFactors | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [triggerSearch, { isFetching: isSearching }] =
    useLazySearchPsfInpatientHospitalsQuery();
  const [previewCmsInpatient, { isLoading: isLoadingPreview }] =
    usePreviewCmsInpatientMutation();

  // ── Hospital search ──────────────────────────────────────────────────────
  // Debounce: trigger search 300ms after typing stops, only for ≥2 chars.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    const handle = window.setTimeout(async () => {
      try {
        const res = await triggerSearch({ year, q }).unwrap();
        setSearchResults(res.data);
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchQuery, year, triggerSearch]);

  // ── Preview: runs when a hospital is selected or year changes ────────────
  const loadPreview = useCallback(
    async (ccn: string) => {
      setPreviewError(null);
      try {
        const res = await previewCmsInpatient({
          year,
          providerCcn: ccn,
          // Note: we pass current overrides so derived factors reflect them.
          overrides: Object.keys(overrides).length ? overrides : undefined,
          toggles,
          drgSampleSize: 0,
        }).unwrap();
        setPsf(res.data.psf);
        setFactors(res.data.factors);
        onPreviewLoaded?.(res.data.psf, res.data.factors);
      } catch (err: any) {
        setPsf(null);
        setFactors(null);
        setPreviewError(
          err?.data?.message ?? "Failed to load PSF data for this hospital."
        );
        onPreviewLoaded?.(null, null);
      }
    },
    [previewCmsInpatient, year, overrides, toggles, onPreviewLoaded]
  );

  useEffect(() => {
    if (providerCcn) {
      loadPreview(providerCcn);
    } else {
      setPsf(null);
      setFactors(null);
      onPreviewLoaded?.(null, null);
    }
    // We intentionally only re-run when CCN or year changes; overrides changes
    // are pushed back into the next manual preview to avoid request thrash.
  }, [providerCcn, year]);

  const handleSelectHospital = (h: IPsfInpatientHospital) => {
    setSelectedHospital(h);
    setSearchQuery("");
    setSearchResults([]);
    onOverridesChange({}); // clear any leftover overrides from previous hospital
    onProviderCcnChange(h.providerCcn);
  };

  const handleClearHospital = () => {
    setSelectedHospital(null);
    onOverridesChange({});
    onProviderCcnChange("");
    setPsf(null);
    setFactors(null);
  };

  // ── Override helpers ──────────────────────────────────────────────────────
  const effective = useMemo(() => {
    if (!psf) return null;
    return { ...psf, ...overrides } as IPsfInpatientRow;
  }, [psf, overrides]);

  const setOverride = <K extends keyof IPsfInpatientRow>(
    key: K,
    value: IPsfInpatientRow[K]
  ) => {
    // If value matches the PSF source, treat as "no override" (clean payload).
    if (psf && psf[key] === value) {
      const next = { ...overrides };
      delete next[key];
      onOverridesChange(next);
    } else {
      onOverridesChange({ ...overrides, [key]: value });
    }
  };

  const isOverridden = (key: keyof IPsfInpatientRow) => key in overrides;

  const resetAllOverrides = () => onOverridesChange({});

  // ── Toggle helpers ────────────────────────────────────────────────────────
  const setToggle = (
    group: keyof ICmsToggleConfig,
    key: string,
    value: boolean
  ) => {
    onTogglesChange({
      ...toggles,
      [group]: { ...toggles[group], [key]: value },
    });
  };

  const resetToggles = () => onTogglesChange(DEFAULT_CMS_TOGGLES);

  const overrideCount = Object.keys(overrides).length;

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderNumberField = (args: {
    id: string;
    label: string;
    key: keyof IPsfInpatientRow;
    minFractionDigits?: number;
    maxFractionDigits?: number;
    min?: number;
    max?: number;
    prefix?: string;
  }) => {
    const value = effective ? (effective[args.key] as number | null) : null;
    return (
      <div className="field">
        <Label htmlFor={args.id}>
          {args.label}
          {isOverridden(args.key) && (
            <span className="ml-2 text-xs text-orange-500">(override)</span>
          )}
        </Label>
        <InputNumber
          id={args.id}
          value={value ?? null}
          onValueChange={(e) => setOverride(args.key, (e.value ?? null) as any)}
          className="w-full"
          minFractionDigits={args.minFractionDigits ?? 0}
          maxFractionDigits={args.maxFractionDigits ?? 6}
          min={args.min}
          max={args.max}
          prefix={args.prefix}
        />
      </div>
    );
  };

  const renderStringField = (args: {
    id: string;
    label: string;
    key: keyof IPsfInpatientRow;
  }) => {
    const value = effective ? (effective[args.key] as string | null) : null;
    return (
      <div className="field">
        <Label htmlFor={args.id}>
          {args.label}
          {isOverridden(args.key) && (
            <span className="ml-2 text-xs text-orange-500">(override)</span>
          )}
        </Label>
        <InputText
          id={args.id}
          value={value ?? ""}
          onChange={(e) =>
            setOverride(args.key, (e.target.value || null) as any)
          }
          className="w-full"
        />
      </div>
    );
  };

  return (
    <>
      <SectionTitle>Hospital Search</SectionTitle>
      <div className="field">
        <InputText
          value={
            selectedHospital
              ? selectedHospital.name ?? selectedHospital.providerCcn
              : searchQuery
          }
          onChange={(e) => {
            if (selectedHospital) handleClearHospital();
            setSearchQuery(e.target.value);
          }}
          placeholder="Type hospital name or CCN (min 2 chars)"
          className="w-full"
        />
        {selectedHospital && (
          <div
            className="mt-2 p-2 border-round flex justify-content-between align-items-center"
            style={{ backgroundColor: "var(--surface-ground)" }}
          >
            <div className="flex flex-column">
              <span className="font-semibold">
                {selectedHospital.name ?? "(no name in PSF)"}
              </span>
              <small className="text-500">
                CCN {selectedHospital.providerCcn} · type{" "}
                {selectedHospital.providerType} · PSF effective{" "}
                {selectedHospital.effectiveDate}
              </small>
            </div>
            <Button
              label="Change"
              icon="pi pi-times"
              className="p-button-text p-button-sm"
              onClick={handleClearHospital}
            />
          </div>
        )}
        {!selectedHospital && searchQuery.length >= 2 && (
          <div className="mt-2 flex flex-column gap-1">
            {isSearching && <small className="text-500">Searching…</small>}
            {!isSearching && searchResults.length === 0 && (
              <small className="text-500">
                No matching hospitals in PSF for year {year}. If you expected a
                hit, fetch the PSF snapshot from the Upload Source Files dialog.
              </small>
            )}
            {searchResults.map((h) => (
              <SearchResultRow
                key={h.providerCcn}
                onClick={() => handleSelectHospital(h)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectHospital(h);
                  }
                }}
              >
                <span className="font-semibold">{h.name ?? "(no name)"}</span>
                <small className="text-500">
                  CCN {h.providerCcn} · type {h.providerType} · {h.stateCode}
                </small>
              </SearchResultRow>
            ))}
          </div>
        )}
      </div>

      {previewError && (
        <div className="p-2 mt-2 border-round surface-ground text-sm text-700 border-left-3 border-orange-500">
          <i className="pi pi-exclamation-triangle mr-2 text-orange-500" />
          {previewError}
        </div>
      )}

      {isLoadingPreview && (
        <div className="p-2 mt-2 border-round surface-ground text-sm text-700">
          <i className="pi pi-spin pi-spinner mr-2" />
          Loading PSF data and pricer factors…
        </div>
      )}

      {effective && (
        <>
          {overrideCount > 0 && (
            <div className="flex justify-content-end mt-2">
              <Button
                label={`Reset ${overrideCount} override${
                  overrideCount === 1 ? "" : "s"
                }`}
                icon="pi pi-undo"
                className="p-button-text p-button-sm"
                style={{ fontSize: "0.75rem" }}
                onClick={resetAllOverrides}
              />
            </div>
          )}
          <CollapsibleSection title="Provider Details" defaultCollapsed>
            <Grid3>
              {renderStringField({
                id: "cms-providerType",
                label: "Provider Type",
                key: "providerType",
              })}
              {renderStringField({
                id: "cms-cbsaActual",
                label: "Geographic CBSA",
                key: "cbsaActualGeographicLocation",
              })}
              {renderStringField({
                id: "cms-cbsaReclass",
                label: "Reclassification CBSA",
                key: "cbsaWageIndexLocation",
              })}
              {renderStringField({
                id: "cms-state",
                label: "State",
                key: "stateCode",
              })}
              {renderStringField({
                id: "cms-county",
                label: "County",
                key: "countyCode",
              })}
              {renderNumberField({
                id: "cms-beds",
                label: "Bed Size",
                key: "bedSize",
                minFractionDigits: 0,
                maxFractionDigits: 0,
                min: 0,
              })}
            </Grid3>
          </CollapsibleSection>

          <CollapsibleSection
            title="PPS Factors & Adjustments"
            defaultCollapsed
          >
            <Grid3>
              {renderNumberField({
                id: "cms-opCcr",
                label: "Operating CCR",
                key: "operatingCostToChargeRatio",
                minFractionDigits: 3,
                maxFractionDigits: 6,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-capCcr",
                label: "Capital CCR",
                key: "capitalCostToChargeRatio",
                minFractionDigits: 3,
                maxFractionDigits: 6,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-medicaid",
                label: "Medicaid Ratio",
                key: "medicaidRatio",
                minFractionDigits: 4,
                maxFractionDigits: 6,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-ssi",
                label: "SSI Ratio",
                key: "supplementalSecurityIncomeRatio",
                minFractionDigits: 4,
                maxFractionDigits: 6,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-internsToBeds",
                label: "Interns-to-Beds Ratio",
                key: "internsToBedsRatio",
                minFractionDigits: 4,
                maxFractionDigits: 6,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-capIme",
                label: "Capital IME Ratio (≤ 1.5)",
                key: "capitalIndirectMedicalEducationRatio",
                minFractionDigits: 4,
                maxFractionDigits: 6,
                min: 0,
                max: 1.5,
              })}
              {/* Wage Index intentionally omitted here — the pricer derives it
                  from the CBSA wage-index table (cbsaWageIndexLocation), not
                  from PSF.supplementalWageIndex. The Pricer-Derived Factors
                  block below shows the actual value used. */}
              {renderNumberField({
                id: "cms-cola",
                label: "Cost-of-Living Adjustment",
                key: "costOfLivingAdjustment",
                minFractionDigits: 3,
                maxFractionDigits: 4,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-vbp",
                label: "VBP Adjustment",
                key: "vbpAdjustment",
                minFractionDigits: 4,
                maxFractionDigits: 8,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-hrr",
                label: "HRR Adjustment",
                key: "hrrAdjustment",
                minFractionDigits: 4,
                maxFractionDigits: 6,
                min: 0,
              })}
              {renderNumberField({
                id: "cms-lowVolume",
                label: "Low-Volume Factor",
                key: "lowVolumeAdjustmentFactor",
                minFractionDigits: 4,
                maxFractionDigits: 6,
                min: 0,
                max: 1,
              })}
              {renderNumberField({
                id: "cms-ucp",
                label: "Uncompensated Care / claim",
                key: "uncompensatedCareAmount",
                minFractionDigits: 2,
                maxFractionDigits: 2,
                min: 0,
                prefix: "$",
              })}
            </Grid3>
          </CollapsibleSection>

          <CollapsibleSection
            title="Hospital-Specific Rates (SCH / MDH only)"
            defaultCollapsed
          >
            <Grid2>
              {renderNumberField({
                id: "cms-hsp",
                label: "PPS Facility-Specific Rate (Operating HSP)",
                key: "caseMixAdjustedCostPerDischarge_PpsFacilitySpecificRate",
                minFractionDigits: 2,
                maxFractionDigits: 2,
                min: 0,
                prefix: "$",
              })}
              {renderNumberField({
                id: "cms-capHsp",
                label: "Hospital-Specific Capital Rate",
                key: "hospitalSpecificCapitalRate",
                minFractionDigits: 2,
                maxFractionDigits: 2,
                min: 0,
                prefix: "$",
              })}
            </Grid2>
          </CollapsibleSection>

          <CollapsibleSection title="Quality / EHR Flags" defaultCollapsed>
            <Grid2>
              {renderStringField({
                id: "cms-quality",
                label: "Hospital Quality Indicator (1 = submitted)",
                key: "hospitalQualityIndicator",
              })}
              {renderStringField({
                id: "cms-ehr",
                label: "EHR Reduction Indicator (Y = NOT EHR user)",
                key: "ehrReductionIndicator",
              })}
            </Grid2>
          </CollapsibleSection>

          <CollapsibleSection
            title="Pass-through Amounts (per claim)"
            defaultCollapsed
          >
            <Grid2>
              {renderNumberField({
                id: "cms-pt-dgme",
                label: "Direct Graduate Medical Education",
                key: "passThroughAmountForDirectGraduateMedicalEducation",
                minFractionDigits: 2,
                maxFractionDigits: 2,
                min: 0,
                prefix: "$",
              })}
              {renderNumberField({
                id: "cms-pt-organ",
                label: "Organ Acquisition",
                key: "passThroughAmountForOrganAcquisition",
                minFractionDigits: 2,
                maxFractionDigits: 2,
                min: 0,
                prefix: "$",
              })}
              {renderNumberField({
                id: "cms-pt-allogen",
                label: "Allogeneic Stem Cell",
                key: "passThroughAmountForAllogenicStemCellAcquisition",
                minFractionDigits: 2,
                maxFractionDigits: 2,
                min: 0,
                prefix: "$",
              })}
              {renderNumberField({
                id: "cms-pt-supply",
                label: "Supply Chain",
                key: "passThroughAmountForSupplyChain",
                minFractionDigits: 2,
                maxFractionDigits: 2,
                min: 0,
                prefix: "$",
              })}
            </Grid2>
          </CollapsibleSection>
        </>
      )}

      {factors && (
        <CollapsibleSection
          title="Pricer-Derived Factors (read-only)"
          defaultCollapsed
        >
          <div
            className="p-2 border-round text-sm"
            style={{ backgroundColor: "var(--surface-ground)" }}
          >
            <Grid3>
              <div>
                <small className="text-500">Wage Index</small>
                <div className="font-semibold">
                  {factors.wageIndex.toFixed(4)}
                </div>
              </div>
              <div>
                <small className="text-500">GAF</small>
                <div className="font-semibold">{factors.gaf.toFixed(4)}</div>
              </div>
              <div>
                <small className="text-500">Scenario · Table</small>
                <div className="font-semibold">
                  {SCENARIO_LABELS[factors.scenario] ?? factors.scenario} ·
                  Table {factors.tableType}
                </div>
              </div>
              <div>
                <small className="text-500">Labor / Non-Labor</small>
                <div className="font-semibold">
                  ${factors.laborAmount.toFixed(2)} / $
                  {factors.nonLaborAmount.toFixed(2)}
                </div>
              </div>
              <div>
                <small className="text-500">Capital Base</small>
                <div className="font-semibold">
                  ${factors.capitalBaseRate.toFixed(2)}
                </div>
              </div>
              <div>
                <small className="text-500">HSP applies?</small>
                <div className="font-semibold">
                  {factors.appliesHsp ? "Yes" : "No"}
                </div>
              </div>
              <div>
                <small className="text-500">Operating IME factor</small>
                <div className="font-semibold">
                  {(factors.operatingImeFactor * 100).toFixed(3)}%
                </div>
              </div>
              <div>
                <small className="text-500">Capital IME factor</small>
                <div className="font-semibold">
                  {(factors.capitalImeFactor * 100).toFixed(3)}%
                </div>
              </div>
              <div>
                <small className="text-500">Op DSH / Cap DSH</small>
                <div className="font-semibold">
                  {(factors.operatingDshFactor * 100).toFixed(3)}% /{" "}
                  {(factors.capitalDshFactor * 100).toFixed(3)}%
                </div>
              </div>
            </Grid3>
          </div>
        </CollapsibleSection>
      )}

      <SectionTitle>
        Include in Base Rate
        <Button
          label="Reset to defaults"
          icon="pi pi-undo"
          className="p-button-text p-button-sm ml-2"
          style={{ fontSize: "0.75rem" }}
          onClick={resetToggles}
        />
      </SectionTitle>

      <div className="grid">
        <div className="col-12 md:col-6">
          <div className="font-semibold mb-2">Capital Amounts</div>
          <ToggleCheckbox
            id="cms-tog-cap-fsp"
            label="Capital Federal Specific Portion"
            hint="× DRG weight × GAF"
            checked={toggles.capital.fsp}
            onChange={(v) => setToggle("capital", "fsp", v)}
          />
          <ToggleCheckbox
            id="cms-tog-cap-ime"
            label="Capital IME"
            hint={
              factors
                ? `${(factors.capitalImeFactor * 100).toFixed(
                    3
                  )}% of capital FSP`
                : undefined
            }
            checked={toggles.capital.ime}
            onChange={(v) => setToggle("capital", "ime", v)}
          />
          <ToggleCheckbox
            id="cms-tog-cap-dsh"
            label="Capital DSH"
            hint={
              factors
                ? `${(factors.capitalDshFactor * 100).toFixed(
                    3
                  )}% of capital FSP`
                : undefined
            }
            checked={toggles.capital.dsh}
            onChange={(v) => setToggle("capital", "dsh", v)}
          />
        </div>
        <div className="col-12 md:col-6">
          <div className="font-semibold mb-2">Operating Amounts</div>
          <ToggleCheckbox
            id="cms-tog-op-fsp"
            label="Operating Federal Specific Portion"
            hint="× DRG weight (wage-adjusted)"
            checked={toggles.operating.fsp}
            onChange={(v) => setToggle("operating", "fsp", v)}
          />
          <ToggleCheckbox
            id="cms-tog-op-hsp"
            label="Operating Hospital Specific Payment (HSP)"
            hint={hspHint(factors)}
            checked={toggles.operating.hsp}
            onChange={(v) => setToggle("operating", "hsp", v)}
          />
          <ToggleCheckbox
            id="cms-tog-op-ime"
            label="Operating IME"
            hint={
              factors
                ? `${(factors.operatingImeFactor * 100).toFixed(
                    3
                  )}% of operating FSP`
                : undefined
            }
            checked={toggles.operating.ime}
            onChange={(v) => setToggle("operating", "ime", v)}
          />
          <ToggleCheckbox
            id="cms-tog-op-dsh"
            label="Operating DSH"
            hint={
              factors
                ? `${(factors.operatingDshFactor * 100).toFixed(
                    3
                  )}% of operating FSP`
                : undefined
            }
            checked={toggles.operating.dsh}
            onChange={(v) => setToggle("operating", "dsh", v)}
          />
          <ToggleCheckbox
            id="cms-tog-op-ucp"
            label="Uncompensated Care"
            hint={
              effective?.uncompensatedCareAmount != null
                ? `$${effective.uncompensatedCareAmount.toFixed(2)} / claim`
                : undefined
            }
            checked={toggles.operating.ucp}
            onChange={(v) => setToggle("operating", "ucp", v)}
          />
          <ToggleCheckbox
            id="cms-tog-op-hrr"
            label="Readmission Adjustment (HRR)"
            hint={
              effective?.hrrAdjustment != null
                ? `× ${effective.hrrAdjustment.toFixed(4)}`
                : undefined
            }
            checked={toggles.operating.hrr}
            onChange={(v) => setToggle("operating", "hrr", v)}
          />
          <ToggleCheckbox
            id="cms-tog-op-vbp"
            label="Value-Based Purchasing (VBP)"
            hint={
              effective?.vbpAdjustment != null
                ? `× ${effective.vbpAdjustment.toFixed(6)}`
                : undefined
            }
            checked={toggles.operating.vbp}
            onChange={(v) => setToggle("operating", "vbp", v)}
          />
          <ToggleCheckbox
            id="cms-tog-op-newtech"
            label="New Technology Add-On"
            hint="per DRG when applicable"
            checked={toggles.operating.newTech}
            onChange={(v) => setToggle("operating", "newTech", v)}
          />
        </div>

        <div className="col-12 md:col-6">
          <div className="font-semibold mb-2">Pass-through Amounts</div>
          <ToggleCheckbox
            id="cms-tog-pt-dgme"
            label="Direct Graduate Medical Education"
            hint={
              effective?.passThroughAmountForDirectGraduateMedicalEducation !=
              null
                ? `$${effective.passThroughAmountForDirectGraduateMedicalEducation.toFixed(
                    2
                  )} / claim`
                : undefined
            }
            checked={toggles.passThrough.dgme}
            onChange={(v) => setToggle("passThrough", "dgme", v)}
          />
          <ToggleCheckbox
            id="cms-tog-pt-organ"
            label="Organ Acquisition"
            hint={
              effective?.passThroughAmountForOrganAcquisition != null
                ? `$${effective.passThroughAmountForOrganAcquisition.toFixed(
                    2
                  )} / claim`
                : undefined
            }
            checked={toggles.passThrough.organ}
            onChange={(v) => setToggle("passThrough", "organ", v)}
          />
          <ToggleCheckbox
            id="cms-tog-pt-allogen"
            label="Allogeneic Stem Cell"
            hint={
              effective?.passThroughAmountForAllogenicStemCellAcquisition !=
              null
                ? `$${effective.passThroughAmountForAllogenicStemCellAcquisition.toFixed(
                    2
                  )} / claim`
                : undefined
            }
            checked={toggles.passThrough.allogeneicStem}
            onChange={(v) => setToggle("passThrough", "allogeneicStem", v)}
          />
          <ToggleCheckbox
            id="cms-tog-pt-supply"
            label="Supply Chain"
            hint={
              effective?.passThroughAmountForSupplyChain != null
                ? `$${effective.passThroughAmountForSupplyChain.toFixed(
                    2
                  )} / claim`
                : undefined
            }
            checked={toggles.passThrough.supplyChain}
            onChange={(v) => setToggle("passThrough", "supplyChain", v)}
          />
        </div>
        <div className="col-12 md:col-6">
          <div className="font-semibold mb-2">Other PPS Amounts</div>
          <ToggleCheckbox
            id="cms-tog-other-hac"
            label="HAC Reduction"
            hint={
              (effective?.hacReductionParticipantIndicator ?? "").trim() === "Y"
                ? "−1% of operating FSP"
                : "not a participant"
            }
            checked={toggles.otherPps.hacReduction}
            onChange={(v) => setToggle("otherPps", "hacReduction", v)}
          />
          <ToggleCheckbox
            id="cms-tog-other-lowVolume"
            label="Low-Volume Adjustment"
            hint={
              effective?.lowVolumeAdjustmentFactor != null &&
              effective.lowVolumeAdjustmentFactor > 0
                ? `factor ${effective.lowVolumeAdjustmentFactor.toFixed(6)}`
                : "not eligible"
            }
            checked={toggles.otherPps.lowVolume}
            onChange={(v) => setToggle("otherPps", "lowVolume", v)}
          />
          <ToggleCheckbox
            id="cms-tog-other-ehrReduction"
            label="EHR Reduction (informational)"
            hint="affects std-amount selection"
            checked={toggles.otherPps.ehrReduction}
            onChange={(v) => setToggle("otherPps", "ehrReduction", v)}
          />
        </div>
      </div>
    </>
  );
}

export default HospitalInpatientCmsFields;
